import { useAuth } from "react-oidc-context";
import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { NavLink } from "react-router";
import SearchBar from "./SearchBar.tsx";
import {
  LogIn,
  LogOut,
  MessageCircle,
  Music,
  ShieldUser,
  UserCircle,
} from "lucide-react";
import { ProfilePicture } from "../profile/ProfilePicture.tsx";
import { useLanguageStore } from "../../stores/languageStore.ts";
import { useUserDataQuery } from "../../api/userApi.ts";
import { useChatsQuery } from "../../api/chatApi.ts";

import { findAndActivateLocale, locales } from "../../i18n.ts";
import LanguageSelector from "../LanguagesSelector.tsx";
import { useSupportedLanguages } from "../../api/languageApi.ts";
import { Trans } from "@lingui/react/macro";
import { hasRole } from "../../services/roleService.ts";
import Filter from "./Filter.tsx";
import { useBecomeTeacherStore } from "../../stores/becomeTeacherStore.ts";

const Navbar = () => {
  const auth = useAuth();
  const { language, setLocale } = useLanguageStore();
  const { refetch: refetchSupportedLanguages } = useSupportedLanguages();
  const { data: user } = useUserDataQuery(auth.user?.profile.sub);
  const { data: chats } = useChatsQuery({ refetchInterval: 60000 });
  const { setIsBecomingTeacher } = useBecomeTeacherStore();

  const unreadChats = useMemo(() => {
    if (!chats || chats.length === 0) return 0;
    return chats.reduce((total, chat) => total + (chat.unreadCount || 0), 0);
  }, [chats]);

  // State to manage dropdown visibility for the Menu button
  const [isMenuDropdownOpen, setIsMenuDropdownOpen] = useState(false);
  // Ref for the Menu dropdown container to detect clicks outside
  const menuDropdownRef = useRef<HTMLDivElement>(null);

  // Close Menu dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuDropdownRef.current &&
        !menuDropdownRef.current.contains(event.target as Node)
      ) {
        setIsMenuDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  function login() {
    auth
      .signinRedirect({ redirect_uri: window.location.href })
      .catch(console.error);
  }

  function logout() {
    auth.signoutRedirect().catch(console.error);
  }

  function onLanguageChange(selectedLocale: string) {
    findAndActivateLocale(selectedLocale).then(setLocale).catch(console.error);
    refetchSupportedLanguages().catch(console.error);
  }

  const handleLanguageChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const selectedLocale = event.target.value;
    onLanguageChange(selectedLocale);
  };

  const menuItemClasses =
    "flex items-center w-full px-4 py-2.5 text-sm text-soluud-text-primary hover:bg-soluud-primary-light transition-colors duration-150 rounded-lg mx-1 my-0.5";

  return (
    <nav
      className="border-soluud-border sticky top-0 z-50 h-16 border-b px-6 py-2 shadow-[--shadow-navbar]"
      aria-label="Main navigation"
    >
      {/* Background with blur - separate to avoid creating a containing block for fixed children */}
      <div className="absolute inset-0 -z-10 bg-white/80 backdrop-blur-lg" />
      <div className="relative flex w-full min-w-0 items-center justify-between">
        {/* Left Section - Logo */}
        <div className="flex min-w-0 flex-1 items-center justify-start">
          <NavLink to="/" className="shrink-0">
            <img src="/soluud.svg" alt="Soluud Logo" className="h-12 w-12" />
          </NavLink>
        </div>

        {/* Center Section - SearchBar only to ensure it is perfectly centered */}
        <div className="mx-2 flex-none sm:mx-4">
          <SearchBar />
        </div>

        {/* Right Section - Filter, Language Selector and User Menu */}
        <div className="flex min-w-0 flex-1 items-center justify-start">
          {/* Filter is placed here so it is next to the centered SearchBar */}
          <div className="mr-2 sm:mr-4">
            <Filter />
          </div>

          {/* Spacer to push the rest of the menu to the far right */}
          <div className="flex-1" />

          <div className="flex items-center space-x-2 sm:space-x-4">
            <div className="hidden shrink-0 sm:block">
              <LanguageSelector
                locales={locales}
                locale={language}
                handleLanguageChange={handleLanguageChange}
              />
            </div>

            {/* Menu button with dropdown functionality */}
            <div
              className="relative hidden shrink-0 sm:block"
              ref={menuDropdownRef}
            >
              <button
                onClick={() => setIsMenuDropdownOpen(!isMenuDropdownOpen)}
                className="focus-visible:ring-soluud-primary relative flex h-12 w-12 shrink-0 cursor-pointer items-center justify-center rounded-full p-1 transition-colors duration-150 hover:bg-gray-100 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                aria-haspopup="true"
                aria-expanded={isMenuDropdownOpen}
                aria-label="User menu"
              >
                <ProfilePicture
                  profilePictureUrl={
                    auth.isAuthenticated ? user?.profilePictureUrl : undefined
                  }
                  firstName={user?.firstName}
                  lastName={user?.lastName}
                  className="h-full w-full"
                  iconSize={25}
                  variant="black"
                />

                {/* Notification bubble for unread chats — using warning red for contrast */}
                {auth.isAuthenticated && unreadChats > 0 && (
                  <div className="bg-soluud-warning absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold text-white shadow-sm">
                    {unreadChats > 99 ? "99+" : unreadChats}
                  </div>
                )}
              </button>

              {isMenuDropdownOpen && (
                <div
                  className="border-soluud-border absolute right-0 z-50 mt-2 w-52 origin-top-right animate-[menu-enter_150ms_ease-out] overflow-hidden rounded-xl border bg-white shadow-[--shadow-elevated]"
                  role="menu"
                  aria-orientation="vertical"
                  aria-labelledby="menu-button"
                >
                  <div className="py-1.5" role="none">
                    {/* Language Selector for mobile */}
                    <div className="border-soluud-border-subtle block border-b px-4 py-2 sm:hidden">
                      <LanguageSelector
                        locales={locales}
                        locale={language}
                        handleLanguageChange={handleLanguageChange}
                      />
                    </div>

                    {auth.isAuthenticated ? (
                      <>
                        <NavLink
                          to="/mystudio"
                          className={menuItemClasses}
                          role="menuitem"
                          tabIndex={-1}
                          onClick={() => setIsMenuDropdownOpen(false)}
                        >
                          <UserCircle className="mr-2.5 h-4 w-4 opacity-60" />
                          <Trans>My Studio</Trans>
                        </NavLink>

                        {!user?.teacherStatus && (
                          <button
                            onClick={() => {
                              setIsMenuDropdownOpen(false);
                              setIsBecomingTeacher(true);
                            }}
                            className={`cursor-pointer text-left ${menuItemClasses}`}
                            role="menuitem"
                            tabIndex={-1}
                          >
                            <Music className="mr-2.5 h-4 w-4 opacity-60" />
                            <Trans>Become Teacher!</Trans>
                          </button>
                        )}

                        <NavLink
                          to="/mystudio/messages"
                          className={menuItemClasses}
                          role="menuitem"
                          tabIndex={-1}
                          onClick={() => setIsMenuDropdownOpen(false)}
                        >
                          <MessageCircle className="mr-2.5 h-4 w-4 opacity-60" />
                          <Trans>Messages</Trans>
                          {unreadChats > 0 && (
                            <span className="bg-soluud-warning ml-auto flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold text-white">
                              {unreadChats > 99 ? "99+" : unreadChats}
                            </span>
                          )}
                        </NavLink>

                        <div
                          className="border-soluud-border-subtle mx-3 my-1.5 border-t"
                          role="none"
                        />

                        {hasRole(auth.user, "manage-filters") && (
                          <NavLink
                            to="/backoffice/filters"
                            className={menuItemClasses}
                            role="menuitem"
                            tabIndex={-1}
                            onClick={() => setIsMenuDropdownOpen(false)}
                          >
                            <ShieldUser className="mr-2.5 h-4 w-4 opacity-60" />
                            <Trans>Backoffice</Trans>
                          </NavLink>
                        )}

                        <button
                          onClick={() => {
                            logout();
                            setIsMenuDropdownOpen(false);
                          }}
                          className={`cursor-pointer text-left ${menuItemClasses}`}
                          role="menuitem"
                          tabIndex={-1}
                        >
                          <LogOut className="mr-2.5 h-4 w-4 opacity-60" />
                          <Trans>Sign out</Trans>
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => {
                          login();
                          setIsMenuDropdownOpen(false);
                        }}
                        className={`cursor-pointer text-left ${menuItemClasses}`}
                        role="menuitem"
                        tabIndex={-1}
                      >
                        <LogIn className="mr-2.5 h-4 w-4 opacity-60" />
                        <Trans>Sign in</Trans>
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
